import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { BrowserRouter, useLocation } from 'react-router';
import { confirmMaxPhone, login, loginMax, logout } from '../api/auth';
import { ApiError, type FetchFn } from '../api/client';
import { SessionLostError, createSession } from '../api/session';
import { getProfile } from '../api/tenant';
import { clearWebToken, loadWebToken, rememberWebState, saveWebToken } from '../api/webSession';
import { createdEvent, formOpened, screenOf, track, trackCreate, trackFail } from './metrics';
import { AuthGate } from '../features/auth/AuthGate';
import { WebAuthGate } from '../features/auth/WebAuthGate';
import { WebHandoffScreen } from '../features/auth/WebHandoffScreen';
import { HomeScreen } from '../features/home/HomeScreen';
import { HomeScreenB } from '../features/home/HomeScreenB';
import { NEW_UI } from './features';
import { ObjectGate } from '../features/objects/ObjectGate';
import { InviteScreen } from '../features/invite/InviteScreen';
import { PassCardScreen } from '../features/pass-card/PassCardScreen';
import { CrewScreen } from '../features/pass-form/CrewScreen';
import { NewPassScreen } from '../features/pass-form/NewPassScreen';
import { QuickPassScreen } from '../features/pass-form/QuickPassScreen';
import { RegularScreen } from '../features/pass-form/RegularScreen';
import { AddressesScreen } from '../features/more/AddressesScreen';
import { ConfidancesScreen } from '../features/more/ConfidancesScreen';
import { FeedScreen } from '../features/more/FeedScreen';
import { GuestsScreen } from '../features/more/GuestsScreen';
import { JoinScreen } from '../features/more/JoinScreen';
import { MoreScreen } from '../features/more/MoreScreen';
import { MyCarsScreen } from '../features/more/MyCarsScreen';
import { NotificationsScreen } from '../features/more/NotificationsScreen';
import { ProfileScreen } from '../features/more/ProfileScreen';
import { WhatsNewScreen } from '../features/more/WhatsNewScreen';
import { PassesScreen } from '../features/passes/PassesScreen';
import { NewMessageScreen } from '../features/requests/NewMessageScreen';
import { RequestChatScreen } from '../features/requests/RequestChatScreen';
import { RequestsScreen } from '../features/requests/RequestsScreen';
import type { SignedContact, TelegramApi } from '../telegram/webapp';
import { deepLinkFromInitData, deepLinkRoute } from '../domain/deepLink';
import { handoffUrl, newWebState, webLoginLink, webLoginStateFromInitData, webOrigin } from '../domain/webLogin';
import { DeepLinkRedirect } from './DeepLinkRedirect';
import { Shell } from './Shell';

interface Props {
  /** Telegram.WebApp inside Telegram, telegram/max.ts inside Max, telegram/browser.ts in an ordinary browser. */
  telegram: TelegramApi;
  fetchFn: FetchFn;
  apiBaseUrl: string;
  /** Browser only: the token the mini app has just handed over in the URL fragment (api/webSession.ts). */
  handoff?: { token: string; trusted: boolean } | null;
}

const BASE = import.meta.env.BASE_URL;

/** Browser: forget the token and start over at the login screen (the query string keeps mock scenarios). */
function signOutWeb() {
  clearWebToken(window);
  window.location.replace(BASE + window.location.search);
}

const currentScreen = () => screenOf(window.location.pathname, import.meta.env.BASE_URL);

/** A pass, invite, request or power of attorney created in the app: counted by type and screen (metrics.ts). */
function trackCreated(path: string, options: { method?: string; body?: unknown }) {
  const event = createdEvent(path, options.method, options.body);
  if (event) trackCreate(event, currentScreen());
}

/** The same order refused by mobile-api: counted with the API error code. */
function trackFailed(path: string, options: { method?: string; body?: unknown }, error: ApiError) {
  const event = createdEvent(path, options.method, options.body);
  if (event) trackFail(event, currentScreen(), error.code);
}

/** Opening an order form starts the funnel and the clock for the time to order (metrics.ts). */
function FormMetrics() {
  const { pathname } = useLocation();
  useEffect(() => formOpened(screenOf(pathname)), [pathname]);
  return null;
}

function makeQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        // Lists refresh when the resident comes back to the app (spec §8.3): focus covers visibilitychange.
        refetchOnWindowFocus: true,
        // Business errors and a lost session will not fix themselves; a network blip may.
        retry: (count, error) => !(error instanceof ApiError || error instanceof SessionLostError) && count < 2,
      },
    },
  });
}

interface SignedInProps extends Props {
  token: string;
  relogin: () => Promise<string>;
  onSignOut?: () => void;
}

function SignedIn({ token, telegram, relogin, onSignOut, fetchFn, apiBaseUrl }: SignedInProps) {
  // Opened from «Войти через Telegram» in a browser: hand a token over before showing the app (#4218).
  const [webState] = useState(() => (telegram.messenger === 'telegram' ? webLoginStateFromInitData(telegram.initData) : null));
  const [handingOff, setHandingOff] = useState(webState !== null);
  const prepareWebToken = useCallback(() => login(fetchFn, apiBaseUrl, telegram.initData), [fetchFn, apiBaseUrl, telegram]);
  const session = useMemo(
    () => createSession({ fetchFn, baseUrl: apiBaseUrl, token, relogin, onSuccess: trackCreated, onFailure: trackFailed }),
    // The session keeps its own fresh token after relogin; recreate it only for a new sign-in.
    [],
  );
  // Read once per launch: the signed initData does not change while the app is open.
  const [link] = useState(() => deepLinkFromInitData(telegram.initData));
  if (handingOff && webState) {
    return (
      <WebHandoffScreen
        prepare={prepareWebToken}
        open={(browserToken) => telegram.openLink(handoffUrl(webOrigin(window.location.origin, import.meta.env.VITE_WEB_ORIGIN), BASE, browserToken, webState))}
        onContinue={() => setHandingOff(false)}
      />
    );
  }
  return (
    <ObjectGate
      session={session}
      telegram={telegram}
      linkedObjectId={link?.kind === 'object' ? link.id : undefined}
      linkedPassId={link?.kind === 'pass' ? link.id : undefined}
    >
      {(object, objects, switchTo) => (
        <>
        <DeepLinkRedirect route={deepLinkRoute(link)} />
        <Shell
          telegram={telegram}
          home={NEW_UI ? <HomeScreenB session={session} telegram={telegram} object={object} objects={objects} onSwitchObject={switchTo} /> : <HomeScreen session={session} telegram={telegram} object={object} objects={objects} onSwitchObject={switchTo} />}
          newPass={<NewPassScreen key={object.id} session={session} telegram={telegram} object={object} />}
          invite={<InviteScreen key={object.id} session={session} telegram={telegram} object={object} />}
          passes={<PassesScreen key={object.id} session={session} object={object} />}
          passCard={<PassCardScreen session={session} telegram={telegram} object={object} />}
          requests={<RequestsScreen key={object.id} session={session} object={object} />}
          newRequest={<NewMessageScreen key={object.id} session={session} telegram={telegram} object={object} />}
          requestChat={<RequestChatScreen session={session} telegram={telegram} />}
          more={<MoreScreen key={object.id} session={session} telegram={telegram} object={object} />}
          nested={{
            '/crew': <CrewScreen key={object.id} session={session} telegram={telegram} object={object} />,
            '/regular': <RegularScreen key={object.id} session={session} telegram={telegram} object={object} />,
            '/quick/taxi': <QuickPassScreen key={`taxi-${object.id}`} session={session} telegram={telegram} object={object} kind="taxi" />,
            '/quick/courier': <QuickPassScreen key={`courier-${object.id}`} session={session} telegram={telegram} object={object} kind="courier" />,
            '/notifications': <NotificationsScreen session={session} />,
            '/more/profile': <ProfileScreen session={session} telegram={telegram} onSignOut={onSignOut} />,
            '/more/guests': <GuestsScreen session={session} />,
            '/more/cars': <MyCarsScreen key={object.id} session={session} object={object} />,
            '/more/addresses': <AddressesScreen session={session} />,
            '/more/feed': <FeedScreen session={session} />,
            '/more/join': <JoinScreen session={session} telegram={telegram} />,
            '/more/confidances': <ConfidancesScreen key={object.id} session={session} telegram={telegram} object={object} />,
            '/more/whats-new': <WhatsNewScreen />,
          }}
        />
        </>
      )}
    </ObjectGate>
  );
}

/** Browser: the mini app signs in and opens this page back with a token (domain/webLogin.ts). */
function startWebLogin() {
  const state = newWebState();
  rememberWebState(window, state);
  window.location.assign(webLoginLink(state));
}

/** Browser: there is no initData to sign in again, so a rejected token sends the resident to the login screen. */
function webRelogin(): Promise<string> {
  signOutWeb();
  return new Promise(() => {});
}

/** The /dev mini app works with production data too: make it impossible to confuse with production. */
function DevBadge() {
  return (
    <div
      role="note"
      style={{
        position: 'fixed', top: 'env(safe-area-inset-top, 0px)', right: 8, zIndex: 50, padding: '2px 8px', borderRadius: 8,
        background: '#1c1c1e', color: '#fff', fontSize: 11, fontWeight: 800, letterSpacing: 0.5, pointerEvents: 'none',
      }}
    >
      DEV · боевые данные
    </div>
  );
}

export function App({ telegram, fetchFn, apiBaseUrl, handoff = null }: Props) {
  const [queryClient] = useState(makeQueryClient);
  const doLogin = useMemo(
    () => () =>
      (telegram.messenger === 'max' ? loginMax : login)(fetchFn, apiBaseUrl, telegram.initData).then((token) => {
        track('login');
        return token;
      }),
    [fetchFn, apiBaseUrl, telegram],
  );
  const confirmPhone = useMemo(
    () =>
      telegram.messenger === 'max'
        ? (contact: SignedContact) =>
            confirmMaxPhone(fetchFn, apiBaseUrl, telegram.initData, contact).then((token) => {
              track('login');
              return token;
            })
        : undefined,
    [fetchFn, apiBaseUrl, telegram],
  );

  const describeAccount = useCallback(
    (token: string) =>
      getProfile(createSession({ fetchFn, baseUrl: apiBaseUrl, token, relogin: () => Promise.reject(new Error('No relogin')) })).then(
        (p) => ({ name: [p.firstName, p.lastName].filter(Boolean).join(' '), phone: p.phone }),
      ),
    [fetchFn, apiBaseUrl],
  );

  return (
    <QueryClientProvider client={queryClient}>
      {import.meta.env.VITE_STAGE === 'dev' && <DevBadge />}
      {/* Served at / in production and at /dev/ for the test mini app; routes are relative to it. */}
      <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, '') || '/'}>
        <FormMetrics />
        {telegram.messenger ? (
          <AuthGate telegram={telegram} login={doLogin} confirm={confirmPhone}>
            {(token) => <SignedIn token={token} telegram={telegram} relogin={doLogin} fetchFn={fetchFn} apiBaseUrl={apiBaseUrl} />}
          </AuthGate>
        ) : (
          <WebAuthGate
            token={loadWebToken(window)}
            handoff={handoff}
            describeAccount={describeAccount}
            onSignIn={(token) => {
              saveWebToken(window, token);
              track('login');
            }}
            onStartLogin={startWebLogin}
          >
            {(token) => (
              <SignedIn
                token={token}
                telegram={telegram}
                relogin={webRelogin}
                onSignOut={() => void logout(fetchFn, apiBaseUrl, token).catch(() => null).then(signOutWeb)}
                fetchFn={fetchFn}
                apiBaseUrl={apiBaseUrl}
              />
            )}
          </WebAuthGate>
        )}
      </BrowserRouter>
    </QueryClientProvider>
  );
}
